import { apiClient } from "@/lib/apiClient";
import type {
  ReportFilters,
  ReportsOverviewResponse,
  EducationSummaryResponse,
  EducationPerformanceApiItem,
  ParticipantsResponse,
  ParticipantSortBy,
  ExportReportPayload,
  CategoryComparisonResponse,
  CategoryComparisonItem,
  CategoryComparisonCombinedItem,
  CategoryTreeNode,
  EducationOfferingItem,
  EducationOfferingsResponse,
} from "../types/reports.types";

const BASE = "/api/admin/reports";

/** ISO tarih: YYYY-MM-DD */
function toIsoDate(d: Date | string | undefined): string | undefined {
  if (!d) return undefined;
  const date = typeof d === "string" ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return undefined;
  return date.toISOString().slice(0, 10);
}

/** Ortak query builder */
function buildReportQuery(filters?: ReportFilters): Record<string, unknown> {
  const q: Record<string, unknown> = {};
  if (filters?.dateFrom) q.dateFrom = toIsoDate(filters.dateFrom) ?? filters.dateFrom;
  if (filters?.dateTo) q.dateTo = toIsoDate(filters.dateTo) ?? filters.dateTo;
  if (filters?.departmentIds?.length) q.departmentIds = filters.departmentIds;
  if (filters?.educationIds?.length) q.educationIds = filters.educationIds;
  if (filters?.categoryIds?.length) q.categoryIds = filters.categoryIds;
  if (filters?.personnelType && filters.personnelType !== "all") q.personnelType = filters.personnelType;
  if (filters?.search?.trim()) q.search = filters.search.trim();
  if (filters?.offeringScope && filters.offeringScope !== "all") q.offeringScope = filters.offeringScope;
  if (filters?.offeringScope === "sequence" && filters.offeringSequenceNumber !== undefined) {
    q.offeringSequenceNumber = filters.offeringSequenceNumber;
  }
  return q;
}

export const reportsApi = {
  /**
   * GET /api/admin/reports
   * Raporlar ve analiz verisi (KPI, birim başarı, katılım, sertifika, eğitim performans)
   */
  getOverview: async (filters?: ReportFilters): Promise<ReportsOverviewResponse> => {
    const { data } = await apiClient.get<ReportsOverviewResponse>(BASE, {
      params: buildReportQuery(filters),
    });
    return data ?? {};
  },

  /**
   * GET /api/admin/reports/education-performance
   * Eğitim bazında performans listesi (education, category, enrolledCount, completedCount, completionPercent, averageScore)
   */
  getEducationPerformance: async (
    filters?: ReportFilters,
  ): Promise<{ data: EducationPerformanceApiItem[]; total: number }> => {
    const { data } = await apiClient.get<{ data?: { items?: EducationPerformanceApiItem[] }; count?: number; total?: number }>(
      `${BASE}/education-performance`,
      { params: buildReportQuery(filters) },
    );
    const items = data?.data?.items ?? [];
    const list = Array.isArray(items) ? items : [];
    const total = data?.count ?? data?.total ?? list.length;
    return { data: list, total };
  },

  /**
   * GET /api/admin/reports/education-performance/{educationId}/summary
   * Eğitim detay - header kartları (tamamlama oranı, başarı puanı, sertifika, aktif kullanıcı)
   */
  getEducationSummary: async (
    educationId: string,
    filters?: ReportFilters,
  ): Promise<EducationSummaryResponse> => {
    const { data } = await apiClient.get<EducationSummaryResponse>(
      `${BASE}/education-performance/${educationId}/summary`,
      { params: buildReportQuery(filters) },
    );
    return data ?? {};
  },

  /**
   * GET /api/admin/reports/education-performance/{educationId}/participants
   * Katılımcı performans listesi (sayfalı, sıralanabilir, aramalı)
   */
  getEducationParticipants: async (
    educationId: string,
    params: {
      page?: number;
      limit?: number;
      search?: string;
      sortBy?: ParticipantSortBy;
      sortOrder?: "asc" | "desc";
    } & ReportFilters,
  ): Promise<{ data: unknown[]; total: number }> => {
    const query: Record<string, unknown> = {
      page: params.page ?? 1,
      limit: params.limit ?? 10,
      ...buildReportQuery(params),
    };
    if (params.search?.trim()) query.search = params.search.trim();
    if (params.sortBy) query.sortBy = params.sortBy;
    if (params.sortOrder) query.sortOrder = params.sortOrder;

    const { data } = await apiClient.get<ParticipantsResponse>(
      `${BASE}/education-performance/${educationId}/participants`,
      { params: query },
    );
    const list = Array.isArray(data?.data) ? data.data : [];
    const total = data?.count ?? data?.total ?? list.length;
    return { data: list, total };
  },

  /**
   * GET /api/admin/reports/education-performance/{educationId}/offerings
   * Eğitime ait dönemler (offering) — frontend dönem seçici dropdown'u için.
   */
  getEducationOfferings: async (educationId: string): Promise<EducationOfferingItem[]> => {
    const { data } = await apiClient.get<EducationOfferingsResponse>(
      `${BASE}/education-performance/${educationId}/offerings`,
    );
    return data?.data?.items ?? data?.items ?? [];
  },

  /**
   * GET /api/admin/reports/categories/comparison
   * Kategori bazlı karşılaştırmalı rapor. `includeDescendants=true` ise alt kategoriler
   * toplamda değerlendirilir. Tek kategori seçildiğinde `childrenBreakdown` doldurulur.
   */
  getCategoryComparison: async (params: {
    categoryIds: string[];
    includeDescendants?: boolean;
    filters?: ReportFilters;
  }): Promise<{
    items: CategoryComparisonItem[];
    childrenBreakdown: CategoryComparisonItem[];
    combinedByName: CategoryComparisonCombinedItem[];
  }> => {
    const query: Record<string, unknown> = buildReportQuery(params.filters);
    // Backend expects UUID; filter out placeholder/synthetic ids (e.g. "root").
    const uuidRe = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const validIds = params.categoryIds.filter((id) => uuidRe.test(id));
    if (validIds.length === 0) {
      return { items: [], childrenBreakdown: [], combinedByName: [] };
    }
    query.categoryIds = validIds;
    if (params.includeDescendants) query.includeDescendants = true;
    const { data } = await apiClient.get<CategoryComparisonResponse>(
      `${BASE}/categories/comparison`,
      { params: query },
    );
    return {
      items: data?.data?.items ?? [],
      childrenBreakdown: data?.data?.childrenBreakdown ?? [],
      combinedByName: data?.data?.combinedByName ?? [],
    };
  },

  /**
   * GET /api/client/education-categories/tree
   * Kategori ağacını döner — karşılaştırma bileşeninde multi-select için kullanılır.
   */
  getCategoryTree: async (): Promise<CategoryTreeNode[]> => {
    const { data } = await apiClient.get<{ data?: CategoryTreeNode[]; items?: CategoryTreeNode[] } | CategoryTreeNode[]>(
      "/api/client/education-categories/tree",
    );
    const raw = Array.isArray(data)
      ? data
      : (data?.data ?? (data as { items?: CategoryTreeNode[] })?.items ?? []);
    return Array.isArray(raw) ? raw : [];
  },

  /**
   * POST /api/admin/reports/export
   * Rapor indir (Excel veya PDF); filtreler query ile
   */
  exportReport: async (payload: ExportReportPayload): Promise<Blob> => {
    const params = buildReportQuery(payload);
    params.format = payload.format;

    const res = await apiClient.post<Blob>(`${BASE}/export`, {}, {
      params,
      responseType: "blob",
    });
    return res.data as Blob;
  },
};
